import React from 'react';
import { Split, X, Check } from 'lucide-react';
import { OABLineItem } from '../types/oab';

export type BalanceActionType = 'BACKORDER' | 'DISCARD' | 'REQUOTE_ANOTHER_VENDOR';

export interface RampBalanceWizardModalProps {
  isOpen: boolean;
  onClose: () => void;
  itemsToReceive: OABLineItem[];
  onApplyGlobalBalanceAction: (action: BalanceActionType) => void;
  onSetItemBalanceAction: (idx: number, action: BalanceActionType) => void;
  onConfirm: () => void | Promise<void>;
}

export const RampBalanceWizardModal: React.FC<RampBalanceWizardModalProps> = ({
  isOpen,
  onClose,
  itemsToReceive,
  onApplyGlobalBalanceAction,
  onSetItemBalanceAction,
  onConfirm
}) => {
  if (!isOpen) return null;

  return (
    <div className="fixed inset-0 z-[65] flex items-center justify-center bg-black/85 backdrop-blur-sm p-4 no-print">
      <div className="bg-surfaceHigh border border-blue-500/40 rounded-xl w-full max-w-2xl shadow-2xl overflow-hidden flex flex-col animate-in fade-in zoom-in-95 duration-150">
        {/* Header */}
        <div className="px-5 py-3.5 bg-blue-500/10 border-b border-blue-500/20 flex items-center justify-between">
          <div className="flex items-center gap-2.5">
            <div className="p-1.5 rounded-lg bg-blue-500/20 text-blue-400 border border-blue-500/30">
              <Split className="w-5 h-5" />
            </div>
            <div>
              <h3 className="text-sm font-bold text-slate-100 flex items-center gap-2">
                <span>Asistente de Saldo Tripartito en Rampa</span>
                <span className="text-[10px] font-mono px-1.5 py-0.5 rounded bg-blue-500/20 text-blue-300 border border-blue-500/30">
                  PLAN-040
                </span>
              </h3>
              <p className="text-[11px] text-slate-400">
                Se detectaron insumos con entrega incompleta. Define el destino del saldo faltante:
              </p>
            </div>
          </div>
          <button
            type="button"
            onClick={onClose}
            className="text-slate-400 hover:text-white p-1 rounded-lg hover:bg-surface transition"
          >
            <X className="w-4 h-4" />
          </button>
        </div>

        {/* Content */}
        <div className="p-5 space-y-4 max-h-[75vh] overflow-y-auto">
          {/* Selector Global Rápido en 1 Clic */}
          <div className="p-3 bg-surface border border-borderSubtle rounded-lg space-y-2">
            <span className="text-[11px] font-semibold text-slate-300 block uppercase font-mono">
              ⚡ Acción Global en 1 Clic (Aplica a todas las líneas incompletas):
            </span>
            <div className="grid grid-cols-1 sm:grid-cols-3 gap-2">
              <button
                type="button"
                onClick={() => onApplyGlobalBalanceAction('BACKORDER')}
                className="p-2.5 text-left rounded-lg border border-amber-500/30 bg-amber-500/10 hover:bg-amber-500/20 transition group"
              >
                <div className="text-xs font-bold text-amber-300 flex items-center gap-1.5">
                  <span>⏳ 1. Esperar Próx. Flete</span>
                </div>
                <p className="text-[10px] text-slate-400 mt-1 leading-tight">
                  Mantiene el compromiso del proveedor. El saldo llegará en un flete posterior.
                </p>
              </button>

              <button
                type="button"
                onClick={() => onApplyGlobalBalanceAction('DISCARD')}
                className="p-2.5 text-left rounded-lg border border-red-500/30 bg-red-500/10 hover:bg-red-500/20 transition group"
              >
                <div className="text-xs font-bold text-red-300 flex items-center gap-1.5">
                  <span>🚫 2. Descartar Saldo</span>
                </div>
                <p className="text-[10px] text-slate-400 mt-1 leading-tight">
                  Cierre definitivo. No se recibirá más de este material con este proveedor.
                </p>
              </button>

              <button
                type="button"
                onClick={() => onApplyGlobalBalanceAction('REQUOTE_ANOTHER_VENDOR')}
                className="p-2.5 text-left rounded-lg border border-purple-500/30 bg-purple-500/10 hover:bg-purple-500/20 transition group"
              >
                <div className="text-xs font-bold text-purple-300 flex items-center gap-1.5">
                  <span>🔄 3. Re-cotizar Saldo</span>
                </div>
                <p className="text-[10px] text-slate-400 mt-1 leading-tight">
                  Quiebre de proveedor. Se cierra aquí y se pasa a borrador para cotizar con otro.
                </p>
              </button>
            </div>
          </div>

          {/* Lista Desglosada con selección por renglón */}
          <div className="space-y-2">
            <span className="text-[11px] font-semibold text-slate-300 block uppercase font-mono">
              📋 Detalle por Insumo Incompleto:
            </span>
            <div className="space-y-2">
              {(() => {
                // PLAN-064 (P64-D1): Agrupar fracciones 1:N por solicitud raíz para evaluar el saldo consolidado real
                const familyMap = new Map<string, {
                  primaryIdx: number;
                  item: OABLineItem;
                  approved: number;
                  prevRec: number;
                  recHoySum: number;
                }>();

                itemsToReceive.forEach((it, idx) => {
                  const rootKey = it.parentSolicitudId || (it.id ? it.id.split(/_(?:split|child)_/)[0] : '') || `_idx_${idx}`;
                  const recHoy = it.cantidadRecibidaHoy !== undefined ? it.cantidadRecibidaHoy : (it.cantidadRecibida || 0);
                  const itemApp = it.cantidadAprobada || it.cantidadSolicitada || 0;

                  const existing = familyMap.get(rootKey);
                  if (!existing) {
                    const origApp = it.originalCantidadAprobada ?? itemApp;
                    familyMap.set(rootKey, {
                      primaryIdx: idx,
                      item: it,
                      approved: origApp,
                      prevRec: it.cantidadRecibidaPrevia || 0,
                      recHoySum: recHoy
                    });
                  } else {
                    existing.recHoySum += recHoy;
                    if (!it.isSplitChild) {
                      existing.primaryIdx = idx;
                      existing.item = it;
                    }
                    if (it.originalCantidadAprobada && it.originalCantidadAprobada > existing.approved) {
                      existing.approved = it.originalCantidadAprobada;
                    }
                  }
                });

                return Array.from(familyMap.values()).map(({ primaryIdx: idx, item: it, approved, prevRec, recHoySum: recHoy }) => {
                  const pending = Math.max(0, approved - prevRec);
                  if (pending <= 0 || recHoy >= pending) return null;
                  const saldo = pending - recHoy;
                  const currentAction = it.actionOnRemaining || 'BACKORDER';

                  return (
                    <div
                      key={it.id || idx}
                      className="p-3 bg-surface border border-borderSubtle rounded-lg flex flex-col sm:flex-row sm:items-center justify-between gap-3 text-xs"
                    >
                      <div className="space-y-0.5 min-w-0">
                        <div className="font-semibold text-slate-200 truncate">{it.nombre}</div>
                        <div className="text-[10px] font-mono text-slate-400 flex items-center gap-2">
                          <span>Aprobado: <strong className="text-slate-300">{approved}</strong></span>
                          <span>·</span>
                          <span>Recibido Hoy: <strong className="text-emerald-400">{recHoy}</strong></span>
                          <span>·</span>
                          <span>Faltante: <strong className="text-amber-400 font-bold">{saldo} {it.unidadMedida || 'und'}</strong></span>
                        </div>
                      </div>

                      {/* Selector Tripartito Individual */}
                      <div className="flex items-center gap-1 shrink-0 bg-surfaceHigh p-1 rounded border border-borderSubtle">
                        <button
                          type="button"
                          onClick={() => onSetItemBalanceAction(idx, 'BACKORDER')}
                          className={`px-2 py-1 text-[10px] font-semibold rounded transition ${
                            currentAction === 'BACKORDER'
                              ? 'bg-amber-500/20 text-amber-300 border border-amber-500/40 font-bold'
                              : 'text-slate-400 hover:text-slate-200'
                          }`}
                          title="Esperar saldo en próximo flete"
                        >
                          ⏳ Backorder
                        </button>
                        <button
                          type="button"
                          onClick={() => onSetItemBalanceAction(idx, 'DISCARD')}
                          className={`px-2 py-1 text-[10px] font-semibold rounded transition ${
                            currentAction === 'DISCARD'
                              ? 'bg-red-500/20 text-red-300 border border-red-500/40 font-bold'
                              : 'text-slate-400 hover:text-slate-200'
                          }`}
                          title="Descartar saldo y cerrar orden"
                        >
                          🚫 Descartar
                        </button>
                        <button
                          type="button"
                          onClick={() => onSetItemBalanceAction(idx, 'REQUOTE_ANOTHER_VENDOR')}
                          className={`px-2 py-1 text-[10px] font-semibold rounded transition ${
                            currentAction === 'REQUOTE_ANOTHER_VENDOR'
                              ? 'bg-purple-500/20 text-purple-300 border border-purple-500/40 font-bold'
                              : 'text-slate-400 hover:text-slate-200'
                          }`}
                          title="Re-cotizar saldo faltante con otro proveedor"
                        >
                          🔄 Re-cotizar
                        </button>
                      </div>
                    </div>
                  );
                });
              })()}
            </div>
          </div>
        </div>

        {/* Footer */}
        <div className="px-5 py-3 bg-surface border-t border-borderSubtle flex items-center justify-end gap-2.5">
          <button
            type="button"
            onClick={onClose}
            className="px-3.5 py-2 text-xs text-slate-400 hover:text-slate-200 transition rounded"
          >
            Volver a Revisar
          </button>
          <button
            type="button"
            onClick={onConfirm}
            className="flex items-center gap-2 px-5 py-2 bg-blue-600 hover:bg-blue-500 text-white font-bold rounded-lg text-xs transition shadow active:scale-95"
          >
            <Check className="w-4 h-4" />
            <span>Confirmar Destinos y Asentar Recepción</span>
          </button>
        </div>
      </div>
    </div>
  );
};
